import React, { useEffect, useState } from 'react';

interface AuditEntry {
  id: number;
  user_id: number | null;
  email: string | null;
  timestamp: string;
  ip_address: string | null;
  user_agent: string | null;
  success: boolean;
  failure_reason: string | null;
}

const getLastWeekRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 7);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
};

const AuditScreen: React.FC<{ apiBaseUrl: string; accessToken: string | null }> = ({ apiBaseUrl, accessToken }) => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(getLastWeekRange());

  const [queried, setQueried] = useState(false);
  const [queryError, setQueryError] = useState<string | null>(null);
  const fetchAudit = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBaseUrl}/audit/login?start=${dateRange.start}&end=${dateRange.end}`, {
        headers: accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {},
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setEntries([]);
        setQueryError(errData.message || `Request failed (${res.status})`);
        setQueried(true);
        return;
      }
      const data = await res.json();
      setEntries(Array.isArray(data) ? data : []);
      setQueryError(null);
      setQueried(true);
    } catch (err) {
      setEntries([]);
      setQueryError('Network error');
      setQueried(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-4">Login Audit Login Log</h2>
      <div className="flex gap-4 mb-4 items-end">
        <label>
          Start Date:
          <input type="date" value={dateRange.start} onChange={e => setDateRange(r => ({ ...r, start: e.target.value }))} />
        </label>
        <label>
          End Date:
          <input type="date" value={dateRange.end} onChange={e => setDateRange(r => ({ ...r, end: e.target.value }))} />
        </label>
        <button className="ml-4 px-4 py-2 bg-blue-600 text-white rounded" onClick={fetchAudit} disabled={loading}>
          {loading ? 'Loading...' : 'Get Data'}
        </button>
      </div>
      {queried && !loading && queryError && (
        <div className="text-red-500 mb-4">{queryError}</div>
      )}
      {queried && !loading && entries.length === 0 && (
        <div className="text-gray-500">No audit login records found for this period.</div>
      )}
      {loading ? <div>Loading...</div> : entries.length > 0 && (
        <table className="min-w-full border text-xs">
          <thead>
            <tr>
              <th>ID</th>
              <th>User ID</th>
              <th>Email</th>
              <th>Timestamp</th>
              <th>IP</th>
              <th>User Agent</th>
              <th>Success</th>
              <th>Failure Reason</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(e => (
              <tr key={e.id} className={e.success ? 'bg-green-50' : 'bg-red-50'}>
                <td>{e.id}</td>
                <td>{e.user_id}</td>
                <td>{e.email}</td>
                <td>{e.timestamp}</td>
                <td>{e.ip_address}</td>
                <td>{e.user_agent}</td>
                <td>{e.success ? 'Yes' : 'No'}</td>
                <td>{e.failure_reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AuditScreen;
